(() => {
  if (window.ieGeekListingGifts) return;
  const list = value => Array.isArray(value) ? value : [];
  const id = value => String(value ?? '').split('/').pop();
  const validHandle = value => typeof value === 'string' && /^[a-z0-9][a-z0-9-]*$/.test(value);

  // US Kite publishes spend goals in the consolidated campaign. Its legacy
  // isGBPWidget mirror can contain stale placeholder thresholds; never use it.
  // These are conditional offers for the selected purchase, not cart eligibility.
  const spendOffers = (product, variantId, data, options) => {
    const variant = list(product?.variants).find(item => id(item.id) === id(variantId));
    const quantity = Number(options.quantity);
    if (!variant?.available || !Number.isSafeInteger(variant.price) || variant.price < 0 ||
        !Number.isSafeInteger(quantity) || quantity < 1 || !/^[A-Z]{3}$/.test(options.shopCurrency || '') ||
        options.currency !== options.shopCurrency) return [];
    const subtotal = variant.price * quantity;
    if (!Number.isSafeInteger(subtotal)) return [];
    const emptyRules = value => Array.isArray(value?.rulesList) && value.rulesList.length === 0;
    const offers = [];
    for (const campaign of list(data?.consolidatedCombinedDiscount)) {
      if (!campaign || campaign.isEnabled !== true || campaign.isDeleted || campaign.mode !== 'AUTOMATIC' ||
          campaign.discountFunctionType !== 'CONSOLIDATED_PROGRESS_BAR' ||
          campaign.campaignScheduleData?.campaignSchedule !== 'CONTINUOUS' ||
          campaign.advancedSettings?.autoAddFreeGift !== true || campaign.advancedSettings.onlyUseWidget ||
          campaign.advancedSettings.appliesToEachItem || campaign.isCampaignUrlEnabled ||
          campaign.advancedSettings.addHighestTierGift || campaign.segmentsData?.addHighestTierGift ||
          campaign.appliesOncePerCustomer || campaign.usageLimit != null ||
          !emptyRules(campaign.customerEligibilityRules) ||
          !Array.isArray(campaign.customerSegmentIds) || campaign.customerSegmentIds.length ||
          !list(campaign.salesChannelSettings?.channels).includes('ONLINE_STORE') ||
          !Array.isArray(campaign.discountTriggeredRulesData?.rulesGlobalList) ||
          !campaign.discountTriggeredRulesData.rulesGlobalList.every(emptyRules)) continue;
      const status = campaign.discountData_shopifyResponse;
      if (!status || (status.status && status.status !== 'ACTIVE')) continue;
      const now = Date.now();
      if ((status.startsAt && (!Number.isFinite(Date.parse(status.startsAt)) || Date.parse(status.startsAt) > now)) ||
          (status.endsAt && (!Number.isFinite(Date.parse(status.endsAt)) || Date.parse(status.endsAt) <= now))) continue;
      const matches = [];
      for (const segment of list(campaign.segmentsData?.segmentsList)) {
        const buy = list(segment?.buyXRulesData?.rulesList), get = list(segment?.getYRulesData?.rulesList);
        if (segment?.goalType !== 'FREE_GIFT' || segment.discountType !== 'PERCENTAGE' ||
            Number(segment.discountValue) !== 100 || segment.autoAddFreeGift !== true || segment.appliesToEachItem ||
            ![segment.totalLimit, segment.limitPerProduct, segment.limitPerCart].every(value => Number(value) === 1) ||
            buy.length !== 1 || get.length !== 1) continue;
        const threshold = buy[0]?.ruleValue, target = get[0]?.ruleValue;
        if (buy[0]?.ruleType !== 'discountedCartSubtotal' || buy[0].type !== 'CART' ||
            threshold?.qualifierType !== 'overallSubtotal' || threshold.qualifierOperatorType !== 'greaterThanOrEqualTo' ||
            !/^\d+(?:\.\d{1,2})?$/.test(String(threshold.qualifierValue)) ||
            get[0]?.ruleType !== 'productVariants' || get[0].type !== 'PRODUCT' ||
            target?.operatorType !== 'include' || target.qualifierType !== 'overallQty' ||
            target.qualifierOperatorType !== 'greaterThanOrEqualTo' || Number(target.qualifierValue) !== 1 ||
            list(target.value).length !== 1) continue;
        const minimumSubtotal = Math.round(Number(threshold.qualifierValue) * 100);
        const gift = target.value[0], giftId = id(gift?.variants?.[0]?.variantId);
        if (!Number.isSafeInteger(minimumSubtotal) || minimumSubtotal <= 0 || subtotal < minimumSubtotal ||
            !validHandle(gift?.handle) || list(gift.variants).length !== 1 || !/^\d+$/.test(giftId)) continue;
        matches.push({ handle: gift.handle, variantId: giftId, label: 'Free Gift', minimumSubtotal });
      }
      offers.push(...matches);
    }
    return offers;
  };

  // Mirror only unconditional, per-product gifts. Cart thresholds, codes and
  // customer-specific offers belong to Kite's cart eligibility calculation.
  const resolve = (product, variantId, data, options = {}) => {
    if (Array.isArray(product?.variants) && !product.variants.some(variant => id(variant.id) === id(variantId) && variant.available)) return [];
    const gifts = new Map();
    for (const campaign of list(data?.promotionCampaigns)) {
      if (!campaign || campaign.isGBPWidget || campaign.isEnabled !== true || campaign.isAutoAdd !== true ||
          campaign.discountSettings?.mode !== 'AUTOMATIC' ||
          campaign.freeGiftEligibility !== 'ALL_PRODUCTS' ||
          campaign.campaignScheduleData?.campaignSchedule !== 'CONTINUOUS' ||
          campaign.isCampaignUrlEnabled || campaign.advancedSettings?.onlyUseWidget ||
          !Array.isArray(campaign.conditionsData?.conditionsList) || campaign.conditionsData.conditionsList.length ||
          (campaign.discountData_shopifyResponse?.status && campaign.discountData_shopifyResponse.status !== 'ACTIVE')) continue;
      const matches = [];
      for (const rule of list(campaign.rulesList)) {
        if (!rule || rule.ruleType !== 'BUY_PRODUCT_X' || Number(rule.minProducts) !== 1 ||
            rule.isProductXVariantSelectionEnabled !== true || !(Number(rule.maxQuantity) >= 1) ||
            rule.discountOfferedOnFreeGift?.type !== 'percentage' || Number(rule.discountOfferedOnFreeGift.value) !== 100) continue;
        const eligible = list(rule.productList).some(item => item &&
          ((product?.id && id(item.productId) === id(product.id)) || (product?.handle && item.handle === product.handle)) &&
          list(item.variants).some(variant => variant && id(variant.variantId) === id(variantId)));
        if (!eligible || list(rule.freeGiftProduct).length !== 1) continue;
        const gift = rule.freeGiftProduct[0];
        if (!validHandle(gift?.handle) || list(gift.variants).length !== 1) continue;
        const giftId = id(gift.variants[0]?.variantId);
        if (!/^\d+$/.test(giftId)) continue;
        matches.push({ handle: gift.handle, variantId: giftId, label: 'Free Gift' });
      }
      // Multiple selectable gifts cannot be inferred as multiple promised gifts.
      if (matches.length > 1 && campaign.isMultipleFreeGiftAllowed !== true) continue;
      matches.forEach(gift => gifts.set(gift.handle + ':' + gift.variantId, gift));
    }
    for (const gift of spendOffers(product, variantId, data, options)) {
      const key = gift.handle + ':' + gift.variantId;
      if (!gifts.has(key)) gifts.set(key, gift);
    }
    return [...gifts.values()];
  };

  const products = new Map();
  const load = async descriptor => {
    if (!validHandle(descriptor?.handle)) return null;
    const root = window.Shopify?.routes?.root || window.theme?.routes?.root_url || '/';
    const base = new URL(root, location.origin);
    if (base.origin !== location.origin) return null;
    const url = new URL(base.pathname.replace(/\/?$/, '/') + 'products/' + descriptor.handle + '.js', location.origin);
    if (!products.has(url.href)) {
      products.set(url.href, (async () => {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 10000);
        try {
          const response = await fetch(url.href, { credentials: 'same-origin', signal: controller.signal });
          if (!response.ok) throw new Error('Gift product request failed: ' + response.status);
          return await response.json();
        } catch (error) {
          console.warn('Listing gift product could not be loaded', descriptor.handle, error);
          return null;
        } finally { clearTimeout(timeout); }
      })());
    }
    const product = await products.get(url.href);
    const variant = list(product?.variants).find(item => id(item.id) === id(descriptor.variantId));
    if (!variant?.available || !Number.isFinite(variant.price) || variant.price < 0) return null;
    const source = variant.featured_image || product.featured_image || product.images?.[0];
    const image = typeof source === 'string' ? source : source?.src;
    return {
      id: variant.id,
      title: product.title + (variant.title && variant.title !== 'Default Title' ? ' — ' + variant.title : ''),
      image: typeof image === 'string' && /^(https?:\/\/|\/\/|\/[^/])/.test(image) ? image : '',
      price: variant.price,
      label: descriptor.label || 'Free Gift',
      ...(descriptor.minimumSubtotal ? { minimumSubtotal: descriptor.minimumSubtotal } : {})
    };
  };

  // Kite also removes the global after boot. Keep the page's last data for
  // deferred load events and theme-editor remounts; never persist across pages.
  let lastPageData = null;
  const subscribe = callback => {
    let previousSource, previousRuntime;
    const refresh = () => {
      const source = document.getElementById('kite-app-data')?.textContent || '';
      // Kite moves the JSON into this global and clears the script before
      // deferred theme scripts run. Support both sides of that hand-off.
      const runtime = window.kite_app_data;
      if (source === previousSource && runtime === previousRuntime) return;
      previousSource = source;
      previousRuntime = runtime;
      try {
        if (source.trim()) lastPageData = JSON.parse(source);
        else if (runtime) lastPageData = runtime;
        callback(lastPageData);
      } catch (error) {
        lastPageData = null;
        console.warn('Invalid Kite gift configuration', error); callback(null);
      }
    };
    const containsData = node => node.nodeType === 1 && (node.id === 'kite-app-data' || node.querySelector?.('#kite-app-data'));
    const observer = new MutationObserver(records => {
      if (records.some(record => record.target.id === 'kite-app-data' || record.target.parentElement?.id === 'kite-app-data' ||
          [...record.addedNodes, ...record.removedNodes].some(containsData))) refresh();
    });
    observer.observe(document.documentElement, { childList: true, subtree: true, characterData: true });
    document.addEventListener('DOMContentLoaded', refresh);
    window.addEventListener('load', refresh);
    refresh();
    return () => {
      observer.disconnect();
      document.removeEventListener('DOMContentLoaded', refresh);
      window.removeEventListener('load', refresh);
    };
  };
  window.ieGeekListingGifts = { resolve, load, subscribe };
})();
